import { Form } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { FormGroup } from '../common/form/FormGroup';
import { Input } from '../common/form/Input';
import { PhoneInput } from '../common/form/PhoneInput';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { SubmitButton } from '../common/form/SubmitButton';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsDialog } from '../common/basic/QcsDialog';
import { QcsDialogActions } from '../common/basic/QcsDialogActions';
import { QcsDialogContentText } from '../common/basic/QcsDialogContentText';
import { QcsBox } from '../common/basic/QcsBox';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { ErrorAlert } from '../common/ErrorAlert';

import { validations } from '../../utils/validations';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { resources } from '@api/resources';
import { aresService, supplierEmployeeService, supplierService } from '@api/services/services';
import { SupplierRequest } from '@api/models/suppliers/SupplierRequest';
import { SupplierDto } from '@api/models/suppliers/SupplierDto';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { SupplierState } from '@api/models/enums/SupplierState';
import { UserRole } from '@api/models/enums/UserRole';
import { QcsErrorCode } from '@api/models/enums/QcsErrorCode';
import { getApiErrorCode } from '../../utils/error';

const emptyValues: SupplierRequest = {
  name: '',
  ico: '',
  vat: '',
  note: '',
  address: { street: '', city: '', postalCode: '', country: '' },
  contactName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  contactLanguage: '',
};

/**
 * Request to add a subcontractor.
 *
 * - `new` mode (external manager): fills supplier + responsible person and POSTs a request;
 *   the supplier is created in {@link SupplierState.Requested} state on the backend.
 * - approval mode (internal manager, reached via the email link with a concrete supplierId):
 *   loads the requested supplier + its responsible person read-only and lets the recipient
 *   approve or reject & delete the request.
 */
export const SupplierRequestDetail: FC = () => {
  const { t } = useTranslation();
  const params = useParams<{ invitationId: string; supplierId: string }>();
  const { navigate, goBack } = useNavigation();
  const companyCustomization = useAppSelector(selectCompanyCustomization);

  const isApproval = !!params.supplierId && params.supplierId !== 'new';

  // Loaded entities kept for the approval mode (pre-fill + the approve PUTs).
  const [supplier, setSupplier] = useState<SupplierDto>();
  const [responsiblePerson, setResponsiblePerson] = useState<SupplierEmployeeDto>();
  // Translation key for a blocking message shown instead of the form. Covers the two cases
  // <ShowResourceContent> can't surface: a rejected request (404 code) and an already-handled one (200).
  const [blockMessageKey, setBlockMessageKey] = useState<string>();
  // The request was sent; an info dialog explains the approval step before leaving the page.
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    if (!isApproval) return;

    let cancelled = false;
    const load = async () => {
      try {
        const supplierRes = await supplierService.getById(params.supplierId!);
        if (cancelled) return;

        // The request may already have been approved (or otherwise moved on).
        if (supplierRes.data.state !== SupplierState.Requested) {
          setBlockMessageKey('supplierRequest.alreadyApproved');
          return;
        }

        const employeesRes = await supplierEmployeeService.query({
          supplierId: params.supplierId,
          role: UserRole.ExternalManager,
        });
        if (cancelled) return;

        setSupplier(supplierRes.data);
        setResponsiblePerson(employeesRes.data.content?.[0]);
      } catch (error) {
        if (cancelled) return;
        // The supplier no longer exists (request was rejected). Other errors fall through to
        // <ShowResourceContent>, which resolves them via the shared getErrorMessageKey logic.
        if (getApiErrorCode(error) === QcsErrorCode.SupplierNotFound) {
          setBlockMessageKey('supplierRequest.notFound');
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isApproval, params.supplierId]);

  const initialValues: SupplierRequest = isApproval
    ? {
        name: supplier?.name ?? '',
        ico: supplier?.ico ?? '',
        vat: supplier?.vat,
        note: supplier?.note,
        address: supplier?.address,
        contactName: responsiblePerson?.name ?? '',
        contactLastName: responsiblePerson?.lastName ?? '',
        contactEmail: responsiblePerson?.email ?? '',
        contactPhone: responsiblePerson?.phone ?? '',
        contactLanguage: responsiblePerson?.language ?? '',
      }
    : { ...emptyValues, contactLanguage: companyCustomization?.defaultLanguage ?? '' };

  const handleSubmit = async (data: SupplierRequest) => {
    await supplierService.requestSupplier(params.invitationId!, data);
    setRequestSent(true);
  };

  const handleApprove = async () => {
    await supplierService.approveSupplier(params.supplierId!);
    navigate(PathKey.SupplierDetail, { supplierId: params.supplierId! });
  };

  const handleRejectAndDelete = async () => {
    const deleted = await supplierService.deleteRequestedSupplier(params.supplierId!);
    if (deleted) navigate('/');
  };

  // A blocking message takes precedence over <ShowResourceContent>: the not-found / already-handled
  // cases are terminal, and their message must win even when a resource error is also present.
  if (blockMessageKey) {
    return <ErrorAlert message={t(blockMessageKey)} />;
  }

  return (
    <ShowResourceContent resource={[resources.getSupplier, resources.querySupplierEmployees]}>
      <DetailHeader title="supplierRequest.title" showBackTo />
      <FormikDebounce<SupplierRequest>
        initialValues={initialValues}
        enableReinitialize
        validationSchema={Yup.object().shape({
          name: validations.stringRequired(t),
          ico: validations.companyNoRequired(t),
          vat: validations.companyVatNoOptional(t),
          address: Yup.object({
            street: validations.stringRequired(t),
            city: validations.stringRequired(t),
            postalCode: validations.postalCodeRequired(t),
            country: validations.stringRequired(t),
          }),
          contactName: validations.stringRequired(t),
          contactLastName: validations.stringRequired(t),
          contactEmail: validations.emailRequired(t),
          contactPhone: validations.phoneOptional(t),
          contactLanguage: validations.stringRequired(t),
        })}
        onSubmit={handleSubmit}
      >
        {({ values, setValues }) => {
          const handleLoadFromAres = async () => {
            const res = await aresService.fetchSubjectDetailsFromAres(values.ico!);
            // filter out null values
            const data = Object.fromEntries(Object.entries(res.data).filter(([, value]) => value !== null));
            setValues({ ...values, ...data });
          };

          return (
            <Form>
              <FormContainer>
                <QcsBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'auto', sm: 'auto max-content' },
                    gridTemplateRows: { xs: 'auto auto', sm: 'auto' },
                    rowGap: { xs: '1rem', sm: 0 },
                    columnGap: isApproval ? 0 : { xs: 0, sm: '1rem' },
                  }}
                >
                  <Input name="ico" label={t('supplier.ico')} maxLength={14} required removeSpaces disabled={isApproval} />
                  {!isApproval && (
                    <QcsLoadingButton
                      onClick={handleLoadFromAres}
                      disabled={!values.ico}
                      resourceLoading={resources.fetchSubjectDetailsFromAres}
                      variant="contained"
                      sx={{ height: '3.5rem' }}
                    >
                      {t('admin.company.loadFromAres')}
                    </QcsLoadingButton>
                  )}
                </QcsBox>
                <ShowError resource={resources.fetchSubjectDetailsFromAres} />

                <Input name="name" label={t('supplier.name')} maxLength={100} required disabled={isApproval} />
                <Input name="vat" label={t('supplier.vat')} maxLength={14} disabled={isApproval} />

                <FormGroup name="address.title">
                  <Input name="address.street" label={t('address.street')} maxLength={100} required disabled={isApproval} />
                  <Input name="address.city" label={t('address.city')} maxLength={100} required disabled={isApproval} />
                  <Input name="address.postalCode" label={t('address.postalCode')} maxLength={128} required disabled={isApproval} />
                  <Input name="address.country" label={t('address.country')} maxLength={100} required disabled={isApproval} />
                </FormGroup>

                <Input name="note" label={t('supplier.note')} maxLength={250} multiline rows={4} disabled={isApproval} />

                <FormGroup name="supplierRequest.responsiblePerson">
                  <Input name="contactName" label={t('supplierEmployee.name')} maxLength={100} required disabled={isApproval} />
                  <Input name="contactLastName" label={t('supplierEmployee.lastName')} maxLength={100} required disabled={isApproval} />
                  <PhoneInput name="contactPhone" label={t('supplierEmployee.phone')} disabled={isApproval} />
                  <Input name="contactEmail" label={t('supplierEmployee.email')} maxLength={100} required removeSpaces disabled={isApproval} />
                  <LanguageSelect
                    name="contactLanguage"
                    label={t('user.language')}
                    languages={SUPPORTED_LANGUAGES}
                    allowedLanguages={companyCustomization?.applicationLanguages}
                    required
                    disabled={isApproval}
                  />
                </FormGroup>

                <ShowError resource={resources.requestSupplier} />

                {isApproval ? (
                  <QcsBox sx={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'flex-end' }}>
                    <QcsLoadingButton variant="outlined" color="error" onClick={handleRejectAndDelete}>
                      {t('supplierRequest.rejectAndDelete')}
                    </QcsLoadingButton>
                    <QcsLoadingButton variant="contained" onClick={handleApprove} resourceLoading={resources.approveSupplier}>
                      {t('supplierRequest.approve')}
                    </QcsLoadingButton>
                  </QcsBox>
                ) : (
                  <SubmitButton />
                )}
              </FormContainer>
            </Form>
          );
        }}
      </FormikDebounce>

      <QcsDialog open={requestSent} onClose={() => goBack()}>
        <QcsDialogContentText>{t('supplierRequest.pendingApprovalInfo')}</QcsDialogContentText>
        <QcsDialogActions>
          <QcsButton variant="contained" onClick={() => goBack()}>
            {t('common.ok')}
          </QcsButton>
        </QcsDialogActions>
      </QcsDialog>
    </ShowResourceContent>
  );
};
